import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Form, Input, Radio, Switch, Tag } from 'antd';
import { IconMapPins } from '@tabler/icons-react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { CrudMode } from '@ohpen/enso-ui';

import { TradingAddress } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { AddressField } from './AddressField';
import { BDMField } from './BDMField';
import styles from '../styles.module.css';

export const TradingNames: React.FC<TabProps> = ({ form, mode, selectsOptions, fcaModifiedFields, removeFcaModifiedField }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>('Active');
  const listAddRef = useRef<((defaultValue?: any) => void) | null>(null);
  const readonly = mode === CrudMode.VIEW;
  const registrationType = Form.useWatch(['registrationDetails', 'type'], form);
  const isFcaRegistered = registrationType !== 'NOT_FCA_REGISTERED' && registrationType != null;
  const tradingNames = Form.useWatch(['tradingNames'], form) ?? [];
  const totalCount = tradingNames.length;
  const filteredCount = tradingNames.filter((item: TradingAddress) =>
    filter === 'Active' ? item?.isActive !== false : item?.isActive === false,
  ).length;

  const onCreate = useCallback(() => {
    listAddRef.current?.({ isActive: true });
  }, []);

  return (
    <CrudFormCard
      icon={<IconMapPins />}
      title={`${t('lender.firms.labels.tradingNames')}:`}
      extra={
        <Flex gap="8px" justify="space-between" className={styles.tradingNamesExtra}>
          <Flex align="center" gap="8px">
            <span style={{ minWidth: '2em', textAlign: 'center' }}>
              {filter === 'All' ? totalCount : `${filteredCount} / ${totalCount}`}
            </span>
            <Radio.Group
              value={filter}
              onChange={e => setFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
              options={['Active', 'Inactive', 'All']}
            />
          </Flex>
          {!readonly && filter !== 'Inactive' && (
            <Button icon={<PlusOutlined />} onClick={onCreate} size="small">
              {t('global.buttons.add')}
            </Button>
          )}
        </Flex>
      }
      bodyScroll
    >
      <Form.List name="tradingNames">
        {(fields, { add, remove }) => {
          listAddRef.current = add;
          return fields.map(field => {
            const row = tradingNames[field.name];
            const isActive = row?.isActive !== false;
            const isUsed = !!row?.isUsed;
            const isPersisted = !!row?.id;
            const show = filter === 'All' || (filter === 'Active' ? isActive : !isActive);

            return (
              <div key={field.key} className={styles.tradingName} style={show ? undefined : { display: 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item
                      name={[field.name, 'name']}
                      label={
                        <>
                          {t('lender.firms.labels.name')}
                          {isFcaRegistered && isActive && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              {t('lender.firms.labels.fca')}
                            </Tag>
                          )}
                        </>
                      }
                      rules={readonly ? [] : [{ required: true, message: 'Trading name is required' }]}
                      style={{ flex: 1, marginBottom: 16 }}
                    >
                      <Input readOnly={readonly} />
                    </Form.Item>
                    {readonly ? (
                      <Form.Item
                        label={t('lender.firms.labels.status')}
                        style={{ flex: 1, marginBottom: 16 }}
                      >
                        <Tag color={isActive ? 'green' : 'red'}>
                          {isActive ? t('lender.firms.labels.active') : t('lender.firms.labels.inactive')}
                        </Tag>
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name={[field.name, 'isActive']}
                        label={t('lender.firms.labels.status')}
                        tooltip={isUsed ? t('lender.firms.labels.tradingNameDeactivateTooltip') : undefined}
                        valuePropName="checked"
                        style={{ flex: 1, marginBottom: 16 }}
                      >
                        <Switch
                          checkedChildren={t('lender.firms.labels.active')}
                          unCheckedChildren={t('lender.firms.labels.inactive')}
                          disabled={isUsed}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <AddressField
                    readonly={readonly}
                    form={form}
                    getAddress={() => form?.getFieldValue(['tradingNames', field.name])}
                    fcaModifiedFields={fcaModifiedFields}
                    removeFcaModifiedField={removeFcaModifiedField}
                    isFromFca={isActive}
                    namePrefix={[field.name]}
                  />
                  <Form.Item
                    name={[field.name, 'bdm']}
                    style={{ marginBottom: 16 }}
                  >
                    <BDMField readonly={readonly} />
                  </Form.Item>
                </div>
                {!readonly && !isPersisted && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                    title="Remove"
                  />
                )}
              </div>
            );
          });
        }}
      </Form.List>
    </CrudFormCard>
  );
};
